import { DomainError } from '../errors.js';

/**
 * Yönetici hesabının kaba kuvvet kilidi (ADR 0039 §6). Sayaç hesap başına kalıcıdır
 * (`platform_admins`); süreç yeniden başlasa da kilit korunur. Saat parametreyle gelir.
 *
 * - Sayılanlar: yanlış parola, yanlış TOTP, yanlış kurtarma kodu ve kritik işlemde yanlış parola
 *   (ADR 0039 §8). Hepsi aynı `failure` olayıdır.
 * - Ardışık 5 hata 15 dakika kilitler; kilit açıldıktan sonraki 5 hata süreyi ikiye katlar, en çok
 *   24 saat. NIST 800-63B ardışık hatayı ≤100 ile sınırlar ve artan bekleme önerir
 *   [NIST 800-63B 5.2.2]; burada sınır daha sıkıdır (hesap sayısı az).
 * - Yalnız başarılı **tam** giriş (parola + ikinci adım) sayacı ve düzeyi sıfırlar. Doğru parola
 *   tek başına sıfırlamaz; yoksa parolayı bilen biri TOTP'yi sınırsız deneyebilirdi.
 * - Kilit sürerken gelen deneme sayılmaz ve kilidi uzatmaz (çağıran doğrulamaya hiç geçmez).
 */

export const ADMIN_LOCK_THRESHOLD = 5;
const BASE_LOCK_MS = 15 * 60_000;
const MAX_LOCK_MS = 24 * 3_600_000;

export interface LockState {
  /** Son kilitten (ya da son başarılı girişten) beri ardışık hata sayısı. */
  failedAttempts: number;
  /** Kaç kez kilitlendiği; süre buna göre katlanır. */
  lockLevel: number;
  /** Kilidin bittiği an (UTC ISO-8601); kilit yoksa `null`. */
  lockedUntil: string | null;
}

export type LockEvent = 'failure' | 'success';

export const INITIAL_LOCK_STATE: LockState = Object.freeze({
  failedAttempts: 0,
  lockLevel: 0,
  lockedUntil: null,
});

function parseTime(value: string): number {
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) throw new DomainError('INVALID_TIME', value);
  return ms;
}

function assertState(state: LockState): void {
  const ok =
    Number.isInteger(state.failedAttempts) &&
    state.failedAttempts >= 0 &&
    Number.isInteger(state.lockLevel) &&
    state.lockLevel >= 0 &&
    (state.lockedUntil === null || !Number.isNaN(Date.parse(state.lockedUntil)));
  if (!ok) throw new DomainError('INVALID_LOCK_STATE');
}

/** `level`. kilidin süresi: 15 dk × 2^(level−1), en çok 24 saat. */
export function lockDurationMs(level: number): number {
  if (!Number.isInteger(level) || level < 1) throw new DomainError('INVALID_LOCK_STATE');
  // Büyük düzeyde taşmayı önlemek için üs sınırlanır (2^7 × 15 dk = 32 saat > 24 saat)
  return Math.min(BASE_LOCK_MS * 2 ** Math.min(level - 1, 16), MAX_LOCK_MS);
}

export function isLocked(state: LockState, now: string): boolean {
  return state.lockedUntil !== null && parseTime(now) < parseTime(state.lockedUntil);
}

export interface LockTransition {
  state: LockState;
  /** Bu olayla kilit başladı: çağıran hesabın bütün oturumlarını kapatır (ADR 0039 §5). */
  lockedNow: boolean;
  /** Olay sayaca eklendi (kilit sürerken gelen hata eklenmez). */
  counted: boolean;
}

export function nextLockState(state: LockState, event: LockEvent, now: string): LockTransition {
  assertState(state);
  const at = parseTime(now);
  if (isLocked(state, now)) return { state, lockedNow: false, counted: false };
  if (event === 'success') {
    return { state: { ...INITIAL_LOCK_STATE }, lockedNow: false, counted: false };
  }
  const failedAttempts = state.failedAttempts + 1;
  if (failedAttempts < ADMIN_LOCK_THRESHOLD) {
    return {
      state: { ...state, failedAttempts, lockedUntil: null },
      lockedNow: false,
      counted: true,
    };
  }
  const lockLevel = state.lockLevel + 1;
  return {
    state: {
      failedAttempts: 0,
      lockLevel,
      lockedUntil: new Date(at + lockDurationMs(lockLevel)).toISOString(),
    },
    lockedNow: true,
    counted: true,
  };
}
